import React from 'react';
import { ArrowRight, CheckCircle2, Sparkles, Zap, BarChart3, Calendar } from 'lucide-react';
import { Button } from '../ui/button';
import { stats } from '../../data/mock';
import { PixZentLogo } from './Header';
import AuditFormSection from './AuditFormSection';

const HeroSection = ({ onBookCall, onGetAudit }) => {
  return (
    <section className="relative min-h-screen pt-32 pb-20 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-violet-50/30 to-pink-50/20" />
      
      {/* Animated Gradient Orbs */}
      <div className="absolute top-20 right-1/4 w-96 h-96 bg-gradient-to-br from-blue-400/20 to-violet-400/20 rounded-full blur-3xl animate-pulse" />
      <div className="absolute bottom-20 left-1/4 w-80 h-80 bg-gradient-to-br from-violet-400/15 to-pink-400/15 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      
      {/* Grid Pattern */}
      <div className="absolute inset-0 bg-[linear-gradient(rgba(139,92,246,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(139,92,246,0.03)_1px,transparent_1px)] bg-[size:60px_60px]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left Content */}
          <div className="space-y-8">
            {/* Badge */}
            <div className="inline-flex items-center px-4 py-2 bg-white/80 backdrop-blur-sm rounded-full border border-violet-100 shadow-sm">
              <Sparkles className="w-4 h-4 text-violet-500 mr-2" />
              <span className="text-sm font-medium text-slate-700">Now Optimizing for ChatGPT, Gemini & Perplexity</span>
            </div>

            {/* Heading */}
            <div className="space-y-4">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight">
                Is Your Brand{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-violet-600 to-pink-500">
                  Visible
                </span>{' '}
                in AI Search?
              </h1>
              <p className="text-lg sm:text-xl text-slate-600 leading-relaxed max-w-xl">
                73% of consumers now use AI assistants to find products and services. Get your business recommended by ChatGPT, Perplexity & Grok.
              </p>
            </div>

            {/* Trust Elements */}
            <div className="flex flex-wrap gap-4">
              {[
                { icon: CheckCircle2, text: 'No Credit Card Required', color: 'text-emerald-500' },
                { icon: Zap, text: 'Results in 48 Hours', color: 'text-amber-500' },
                { icon: BarChart3, text: '100+ Brands Optimized', color: 'text-blue-500' }
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-2 bg-white/60 px-3 py-2 rounded-full border border-slate-100">
                  <item.icon className={`w-4 h-4 ${item.color}`} />
                  <span className="text-sm text-slate-700 font-medium">{item.text}</span>
                </div>
              ))}
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                onClick={onGetAudit}
                className="bg-gradient-to-r from-blue-600 via-violet-600 to-pink-500 hover:from-blue-700 hover:via-violet-700 hover:to-pink-600 text-white shadow-xl shadow-violet-500/25 hover:shadow-violet-500/40 transition-all duration-300 text-lg px-8 py-6 group"
              >
                Get Your Free Audit
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={onBookCall}
                className="border-2 border-violet-200 text-violet-700 hover:bg-violet-50 hover:border-violet-300 transition-all duration-300 text-lg px-8 py-6 gap-2"
              >
                <Calendar className="w-5 h-5" />
                How It Works
              </Button>
            </div>
          </div>

          {/* Right - Compact Audit Form */}
          <div className="lg:pt-8">
            <AuditFormSection variant="compact" />
          </div>
        </div>

        {/* Stats Bar */}
        <div className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((stat, index) => (
            <div
              key={index}
              className="text-center p-6 bg-white/60 backdrop-blur-sm rounded-2xl border border-slate-100 hover:border-violet-200 hover:bg-white transition-all duration-300 group"
            >
              <div className="text-3xl sm:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-violet-600 to-pink-500 group-hover:scale-105 transition-transform">
                {stat.value}
              </div>
              <p className="text-slate-600 text-sm mt-2">{stat.label}</p>
            </div>
          ))}
        </div>

        {/* Trusted By Logos Marquee */}
        <div className="mt-16">
          <p className="text-center text-slate-500 text-sm mb-6">Trusted by 100+ Industry Leaders</p>
          <div className="relative overflow-hidden">
            <div className="flex space-x-12 animate-marquee">
              {['Sara Business Solutions', 'Aadhivedha', 'GOS Heating Services', 'Pimins AI', 'MAF Oman', 'Gravel Banks',
                'TechFlow Solutions', 'Prestige Properties', 'Elite Dental Group', 'Global Logistics Co',
                'Sara Business Solutions', 'Aadhivedha', 'GOS Heating Services', 'Pimins AI', 'MAF Oman', 'Gravel Banks'
              ].map((company, index) => (
                <span key={index} className="text-slate-400 font-medium whitespace-nowrap text-sm">
                  {company}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
